import { ObservableMap } from '@stencil/store';
import {
    defaultLanguage,
    getCachedLanguage,
    getCachedLanguageWithDate,
    getEnvironment,
    getUserLanguage,
    setLanguage,
    setUserLanguage,
    validateLanguageCode,
} from '@uipath/portal-shell-util';

import { LanguageEvent } from '../models/appInsights/events/language.events';
import { State } from '../store/types/state';
import { AccountAndTenantsInstanceResolver } from './accountAndTenants/accountAndTenantsInstanceResolver';
import { AppInsightsInstanceResolver } from './appInsights/appInsightsInstanceResolver';
import {
    isMsi,
    isStandalone,
} from './EnvironmentUtil';
import { EventHandlersInstanceResolver } from './events/eventHandlersInstanceResolver';
import GlobalEventEmitter from './events/globalEventEmitters';
import Logger from './logger/logger';
import { getOrganizationStandardUrl } from './orgStandardUrlHelper';
import {
    getAccessToken,
    sessionStore,
} from './session/sessionStore';

export async function setPortalShellUserLanguage(store: ObservableMap<State>, accessToken: string) {
    if (!accessToken) {
        return;
    }

    const env = getEnvironment();
    const cachedLanguage = getCachedLanguageWithDate(env);
    const validOverrideLocale = validateLanguageCode(store.get('overrideLocale'));
    Logger.info('language', `env: ${env}, cachedLanguage: ${JSON.stringify(cachedLanguage)}, validOverrideLocale: ${validOverrideLocale}`, { state: store.state });

    const organizationStandardUrl = getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true });
    if (!validOverrideLocale && organizationStandardUrl) {
        const msi = isMsi();
        const userLanguage = await getUserLanguage(
            organizationStandardUrl,
            accessToken,
            sessionStore.get('organizationId'),
            sessionStore.get('userId'),
            msi,
        );

        if (!userLanguage || Number(cachedLanguage.date) > Number(userLanguage.date)) {
            Logger.logCustomEvent(LanguageEvent.SetCachedLanguageAsSelectedLanguageOnInit, {
                cachedLanguage: cachedLanguage.language,
                isSettingUserLanguage: !isStandalone(),
            });
            await setSelectedLanguage(store, cachedLanguage.language, {
                accessToken,
                shouldBroadcast: false,
            });
        } else if (userLanguage && cachedLanguage.language !== userLanguage.language) {
            Logger.logCustomEvent(LanguageEvent.SetUserLanguageAsCachedLanguageOnInit, { userLanguage: userLanguage.language });
            setLanguageNoBroadcast(store, userLanguage.language);
        }
    }
}

export async function setSelectedLanguage(store: ObservableMap<State>, language: string, {
    accessToken = undefined,
    shouldBroadcast = true,
}: {
    accessToken?: string;
    shouldBroadcast?: boolean;
} = {}) {
    if (!accessToken || isStandalone()) {
        setLanguage(language);
        return;
    }

    if (!shouldBroadcast) {
        setStoreLanguage(store, language);
    }

    await setUserLanguage(
        language,
        getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true }),
        getAccessToken(accessToken),
        sessionStore.get('organizationId'),
        sessionStore.get('userId'),
        isMsi(),
        { shouldBroadcast },
    );
}

export function getLanguage(store: ObservableMap<State>) {
    return store.get('locale') || getCachedLanguage();
}

export function setLanguageNoBroadcast(store: ObservableMap<State>, language: string) {
    setStoreLanguage(store, language);
    setLanguage(language, { shouldBroadcast: false });
}

export function setStoreLanguage(store: ObservableMap<State>, language: string) {
    language = validateLanguageCode(language) ?? defaultLanguage;
    const currentLanguage = store.get('locale');
    if (language !== currentLanguage) {
        if (!currentLanguage) {
            const selectedLanguageEvent = { selectedLanguageId: language };
            GlobalEventEmitter.emitLanguageLoaded(selectedLanguageEvent);
            EventHandlersInstanceResolver.Instance?.onLanguageLoaded?.(selectedLanguageEvent);
        }
        store.set('locale', language);
        AppInsightsInstanceResolver.Instance?.setLanguage(language);
        AccountAndTenantsInstanceResolver.Instance?.updateMenuItems({ locale: language });
    }
}
